import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import { installStaleChunkReload, isStaleChunkError } from "@/lib/chunk-reload";
import "./index.css";

// Every browser report is tagged alert_source="browser" and recorded in the same
// alert_pages ledger the server sweep writes to. Reason: the sweep can only count
// its own pages, so a browser-raised incident (payment, share fallback, an
// unhandled render error) was invisible to every report we produced — the gap was
// found by counting incidents in a UI. One ledger, both sources, one count.
const SENTRY_DSN =
  "https://d84a1d1f816606f5636b52c48eda9b27@o4511575772692480.ingest.de.sentry.io/4511575793467472";

function recordBrowserPage(eventId: string | undefined, alertKey: string) {
  // Fire-and-forget, and deliberately not through the Supabase client: this must
  // never fail an error path or pull auth into it. The endpoint takes only the
  // page's own receipt and cannot be told which source it came from.
  try {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/record_browser_alert_page`;
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({
        p_alert_key: alertKey,
        p_sentry_event_id: eventId ?? null,
        p_environment: import.meta.env.MODE,
        p_detail: { path: window.location.pathname },
      }),
    }).catch(() => {
      /* an unrecorded page must not become a second error */
    });
  } catch {
    /* ignore */
  }
}

Sentry.init({
  dsn: SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.MODE === "production",
  initialScope: {
    tags: { alert_source: "browser" },
  },
  beforeSend(event) {
    // A STALE CHUNK IS EXPECTED DURING A PUBLISH, SO IT IS NOT A PAGE.
    // It is still reported, at info level, so the volume stays countable — if
    // real sessions start hitting this it is deploy frequency showing up as
    // customer errors, and that is worth seeing. What it must not do is page:
    // an alert that fires on every publish is how a pager gets ignored.
    const staleChunk = (event.exception?.values ?? []).some((v) =>
      isStaleChunkError(new Error(`${v.type ?? ""}: ${v.value ?? ""}`)),
    ) || (typeof event.message === "string" && isStaleChunkError(event.message));
    if (staleChunk) {
      event.level = "info";
      event.tags = { ...event.tags, stale_chunk: "true" };
      return event;
    }

    // Only real error events count as pages; traces and replays are not alerts.
    if (event.level !== "info" && event.type === undefined) {
      const key =
        event.exception?.values?.[0]?.type ??
        (typeof event.message === "string" ? event.message.slice(0, 120) : "browser_error");
      recordBrowserPage(event.event_id, key);
    }
    return event;
  },
});

installStaleChunkReload();

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    fallback={({ resetError }) => (
      <div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>
        <h1>Something went wrong</h1>
        <p>The error has been reported. Try refreshing the page.</p>
        <button onClick={resetError}>Try again</button>
      </div>
    )}
    showDialog={false}
  >
    <App />
  </Sentry.ErrorBoundary>
);
